/**
 * Markdown editor: textarea + live preview side-by-side on desktop, tabbed
 * on mobile. Toolbar wraps the current selection in markdown syntax for
 * Bold / Italic / Link, or inserts a `{+N unit}` delta chip.
 *
 * The preview pane uses the same ArticleSection renderer the read-only
 * detail page uses, so WYSIWYG matches exactly — including the delta-chip
 * plugin and GFM tables. XSS rules from ArticleSection still apply (no
 * raw HTML through ReactMarkdown).
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";

import ArticleBody from "../ArticleBody";
import TableBuilderDialog from "./TableBuilderDialog";
import { parseTableAtCursor, TableModel } from "./tableModel";

interface MarkdownEditorProps {
  heading: string;
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  placeholder?: string;
  /**
   * When set (and we're on md+), the toolbar uses `position: sticky` and
   * pins at this viewport y offset while the editor body scrolls past.
   * Caller is responsible for the value — for the article editor it's the
   * measured height of the page's sticky action toolbar above us, so the
   * two stacks read as one continuous strip of chrome. Undefined keeps the
   * toolbar inline.
   */
  stickyToolbarTop?: number;
}

type WrapOp = { kind: "wrap"; before: string; after: string; defaultText: string };
type InsertOp = { kind: "insert"; text: string };

function applyOperation(
  textarea: HTMLTextAreaElement,
  current: string,
  op: WrapOp | InsertOp,
): { next: string; selStart: number; selEnd: number } {
  const start = textarea.selectionStart ?? current.length;
  const end = textarea.selectionEnd ?? current.length;
  const selected = current.slice(start, end);

  if (op.kind === "insert") {
    const next = current.slice(0, start) + op.text + current.slice(end);
    const caret = start + op.text.length;
    return { next, selStart: caret, selEnd: caret };
  }

  const inner = selected || op.defaultText;
  const wrapped = `${op.before}${inner}${op.after}`;
  const next = current.slice(0, start) + wrapped + current.slice(end);
  const innerStart = start + op.before.length;
  return { next, selStart: innerStart, selEnd: innerStart + inner.length };
}

function ToolbarButton({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title} arrow>
      <IconButton size="small" onClick={onClick} sx={{ borderRadius: "3px" }}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

export default function MarkdownEditor({
  heading,
  value,
  onChange,
  minRows = 10,
  placeholder,
  stickyToolbarTop,
}: MarkdownEditorProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Sticky toolbar: only on md+ (mobile uses tabs and the cramped layout
  // wouldn't benefit from pinning). Sentinel + IO mirrors the action
  // toolbar's pattern so the elevation cue fires the instant the toolbar
  // becomes stuck.
  const toolbarSentinelRef = useRef<HTMLDivElement | null>(null);
  const [toolbarStuck, setToolbarStuck] = useState(false);
  const stickyEnabled = !isMobile && stickyToolbarTop !== undefined;

  useEffect(() => {
    if (!stickyEnabled) {
      setToolbarStuck(false);
      return;
    }
    const el = toolbarSentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setToolbarStuck(!entry.isIntersecting),
      {
        rootMargin: `-${stickyToolbarTop}px 0px 0px 0px`,
        threshold: [0],
      },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [stickyEnabled, stickyToolbarTop]);

  // Table dialog state. `tableEditRange` is non-null when the dialog is
  // editing an existing table block (open via the toolbar with the
  // cursor inside a table); on Insert we splice the new markdown over
  // that range instead of inserting at the cursor.
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableInitial, setTableInitial] = useState<TableModel | undefined>(
    undefined,
  );
  const [tableEditRange, setTableEditRange] = useState<
    { start: number; end: number } | null
  >(null);

  const run = (op: WrapOp | InsertOp) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { next, selStart, selEnd } = applyOperation(ta, value, op);
    onChange(next);
    // Restore selection after React re-renders the textarea.
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    });
  };

  const openTableDialog = () => {
    const ta = textareaRef.current;
    // Try to detect "edit existing table at cursor" first. If the
    // cursor sits in a GFM table block, pre-fill the dialog and
    // remember the source range so the Insert action splices over it.
    if (ta) {
      const cursor = ta.selectionStart ?? value.length;
      const parsed = parseTableAtCursor(value, cursor);
      if (parsed) {
        setTableInitial(parsed.model);
        setTableEditRange({ start: parsed.start, end: parsed.end });
        setTableDialogOpen(true);
        return;
      }
    }
    setTableInitial(undefined);
    setTableEditRange(null);
    setTableDialogOpen(true);
  };

  const onTableInsert = (markdown: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      onChange(value + "\n\n" + markdown + "\n");
      return;
    }
    let next: string;
    let caret: number;
    if (tableEditRange) {
      // Edit-mode: replace the previous table block verbatim.
      next =
        value.slice(0, tableEditRange.start) +
        markdown +
        value.slice(tableEditRange.end);
      caret = tableEditRange.start + markdown.length;
    } else {
      // Insert-mode: drop at cursor, but make sure the table block sits
      // on its own line. Markdown tables don't render if the line
      // immediately above the header row isn't blank.
      const start = ta.selectionStart ?? value.length;
      const end = ta.selectionEnd ?? value.length;
      const prefix = value.slice(0, start);
      const suffix = value.slice(end);
      const needsLeadingBreak =
        prefix.length > 0 && !prefix.endsWith("\n\n");
      const needsTrailingBreak = !suffix.startsWith("\n");
      const block =
        (needsLeadingBreak ? (prefix.endsWith("\n") ? "\n" : "\n\n") : "") +
        markdown +
        (needsTrailingBreak ? "\n\n" : "");
      next = prefix + block + suffix;
      caret = prefix.length + block.length;
    }
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  };

  const toolbar = (
    <>
      {/* Zero-height sentinel just above the toolbar — IO fires when this
          crosses the sticky offset, signaling that the toolbar is now
          pinned. Rendered unconditionally so React doesn't lose the ref
          when stickyEnabled flips. */}
      <Box ref={toolbarSentinelRef} aria-hidden sx={{ height: 0 }} />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          // Opaque so textarea content scrolling underneath the stuck
          // toolbar is fully covered; matches the toolbar's at-rest bg.
          backgroundColor: "#fafafa",
          px: 1,
          py: 0.5,
          alignItems: "center",
          // Sticky branch — only engaged when the caller has provided a
          // sticky offset AND we're on md+ (mobile uses tabs which would
          // make a pinned toolbar awkward). zIndex 10 sits BELOW the
          // page's action toolbar (zIndex 11), so the action bar's drop
          // shadow falls cleanly onto this toolbar when both are stuck.
          ...(stickyEnabled && {
            position: "sticky",
            top: stickyToolbarTop,
            zIndex: 10,
            boxShadow: toolbarStuck
              ? "0 2px 6px rgba(0, 0, 0, 0.05)"
              : "none",
            transition: "box-shadow 180ms ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }),
        }}
      >
      <ToolbarButton
        title="Bold"
        onClick={() =>
          run({ kind: "wrap", before: "**", after: "**", defaultText: "bold" })
        }
      >
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        onClick={() =>
          run({ kind: "wrap", before: "*", after: "*", defaultText: "italic" })
        }
      >
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Link"
        onClick={() =>
          run({
            kind: "wrap",
            before: "[",
            after: "](https://)",
            defaultText: "text",
          })
        }
      >
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Insert / edit table"
        onClick={openTableDialog}
      >
        <TableChartOutlinedIcon fontSize="small" />
      </ToolbarButton>
      <Box sx={{ width: 8 }} />
      <Tooltip title="Insert positive delta chip" arrow>
        <Button
          size="small"
          onClick={() => run({ kind: "insert", text: "{+0.0 unit}" })}
          sx={{
            minWidth: 0,
            px: 1,
            color: "#1b6e2c",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "none",
          }}
          startIcon={<AddIcon fontSize="small" />}
        >
          delta
        </Button>
      </Tooltip>
      <Tooltip title="Insert negative delta chip" arrow>
        <Button
          size="small"
          onClick={() => run({ kind: "insert", text: "{-0.0 unit}" })}
          sx={{
            minWidth: 0,
            px: 1,
            color: "#a62020",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "none",
          }}
          startIcon={<RemoveIcon fontSize="small" />}
        >
          delta
        </Button>
      </Tooltip>
      </Stack>
    </>
  );

  const editor = (
    <TextField
      multiline
      fullWidth
      minRows={minRows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputRef={textareaRef}
      placeholder={placeholder ?? "Markdown content…"}
      variant="outlined"
      // The toolbar already sits flush above; trim the top border so it
      // visually attaches to the toolbar.
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 0,
          fontFamily: "Menlo, Monaco, 'Roboto Mono', monospace",
          fontSize: "0.85rem",
          alignItems: "flex-start",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "none",
        },
      }}
    />
  );

  const preview = (
    <Box
      sx={{
        backgroundColor: "#fff",
        p: 2,
        minHeight: minRows * 24,
        borderTop: { md: "none" },
      }}
    >
      {value.trim() ? (
        <ArticleBody markdown={value} />
      ) : (
        <Box sx={{ color: "text.disabled", fontStyle: "italic" }}>
          Nothing to preview yet.
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        // We need to clip the inner textarea's bottom corners to the
        // card's rounded edges — but we explicitly do NOT want a
        // scroll/overflow container here, because that would anchor any
        // `position: sticky` descendant (the helper toolbar) to this
        // card rather than to the page's scroll source (`<main>`). The
        // card doesn't scroll, so the toolbar would just sit at its
        // natural position instead of pinning to the viewport.
        //
        // The intuitive choice would be `overflow: clip` (per spec it
        // does NOT establish a scroll container, only `hidden`/`auto`/
        // `scroll` do) — but Chrome/Safari empirically treat `clip` as
        // a sticky-anchoring boundary anyway, breaking exactly this
        // pattern. `clip-path` does pure paint-time clipping with no
        // effect on layout, positioning, or sticky behavior. Caveat:
        // `clip-path` does create a new stacking context, which is
        // fine for us (no z-index assumptions cross this boundary).
        clipPath: "inset(0 round 4px)",
        mb: 3,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f5f5f5",
          fontWeight: 700,
          letterSpacing: "0.3px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          color: "text.secondary",
        }}
      >
        {heading}
      </Box>

      {isMobile ? (
        // Mobile: toolbar stays full-width above the Edit/Preview tabs.
        // Pinning behavior is disabled on xs anyway (see stickyEnabled).
        <>
          {toolbar}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 36,
              borderBottom: "1px solid #e0e0e0",
              "& .MuiTab-root": {
                minHeight: 36,
                textTransform: "none",
                fontSize: "0.8rem",
              },
            }}
          >
            <Tab value="edit" label="Edit" />
            <Tab value="preview" label="Preview" />
          </Tabs>
          {tab === "edit" ? editor : preview}
        </>
      ) : (
        // Desktop: two equal flex columns. Left column wraps [toolbar,
        // editor] in a single Box; right column is the preview. We use
        // flex rather than grid here because position:sticky on a
        // descendant inside a grid item can get clipped by the grid
        // area's bounds in some browsers — flex items don't have that
        // problem. The toolbar's containing block becomes the left
        // column (which is tall: toolbar + editor), so it stays pinned
        // for the full editor height. Width is naturally bounded to the
        // left column (50%), so the preview is never covered.
        //
        // `align-items: stretch` (the flex default) makes both columns
        // the same height — the preview pane fills the same vertical
        // extent it did with the old grid layout.
        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              borderRight: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {toolbar}
            {editor}
          </Box>
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>{preview}</Box>
        </Box>
      )}

      <TableBuilderDialog
        open={tableDialogOpen}
        onClose={() => setTableDialogOpen(false)}
        initial={tableInitial}
        onInsert={onTableInsert}
      />
    </Box>
  );
}
